import { useState, useEffect, useRef, useSyncExternalStore } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { ArrowLeft } from "lucide-react";
import LoginSignupModal, { LoginSignupModalView } from "@/components/ui/login-signup";
import { translateAuthError } from "@/lib/auth-errors";
import { SignInPage } from "@/components/ui/sign-in";
import { useAuth } from "@/contexts/AuthContext";
import { authLocationPath, safeAuthReturn, rememberAuthReturn, clearAuthReturn } from "@/lib/auth-navigation";
import { useTranslation } from "react-i18next";
import { passwordRecoveryURL } from "@/lib/password-recovery";

const desktopQuery = "(min-width: 1024px)";
const subscribeDesktop = (onChange: () => void) => {
  const media = window.matchMedia(desktopQuery);
  media.addEventListener("change", onChange);
  return () => media.removeEventListener("change", onChange);
};
const getDesktop = () => window.matchMedia(desktopQuery).matches;

interface AuthModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultTab?: LoginSignupModalView;
  redirectTo?: string;
  standalone?: boolean;
  defaultForgot?: boolean;
}

export function AuthModal({ open, onOpenChange, defaultTab = "login", redirectTo, standalone = false, defaultForgot = false }: AuthModalProps) {
  const [tab, setTab] = useState<LoginSignupModalView>(defaultTab);
  const [loading, setLoading] = useState(false);
  const [forgotMode, setForgotMode] = useState(defaultForgot);
  const [forgotEmail, setForgotEmail] = useState("");
  const [pendingEmail, setPendingEmail] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  const { user, loading: authLoading } = useAuth();
  const { t } = useTranslation("auth");
  const desktop = useSyncExternalStore(subscribeDesktop, getDesktop, () => false);
  const [errorMessage, setErrorMessage] = useState("");
  const [resetSent, setResetSent] = useState(false);
  const [completedPath, setCompletedPath] = useState<string | null>(null);
  const busy = useRef(false);
  const handled = useRef(false);
  const from = (location.state as { from?: { pathname: string; search?: string; hash?: string } } | null)?.from;
  const target = safeAuthReturn(redirectTo || (from ? authLocationPath(from) : authLocationPath(location)));

  useEffect(() => {
    if (open) {
      setTab(defaultTab);
      setForgotMode(defaultForgot);
      setResetSent(false);
      setErrorMessage("");
      handled.current = false;
    }
  }, [defaultTab, defaultForgot, open]);

  // Every existing caller keeps its mobile dialog; desktop callers enter one route.
  useEffect(() => {
    if (!open || !desktop || standalone) return;
    const params = new URLSearchParams({ next: target, back: authLocationPath(location) });
    if (defaultTab === "register") params.set("view", "register");
    if (defaultForgot) params.set("view", "forgot");
    onOpenChange(false);
    navigate(`/entrar?${params}`, { state: location.state });
  }, [open, desktop, standalone, target, defaultTab, defaultForgot, location, navigate, onOpenChange]);

  // Wait for AuthProvider to resolve the session/profile/role before hitting guards.
  useEffect(() => {
    if (!completedPath || !user || authLoading || handled.current) return;
    handled.current = true;
    if (!completedPath.startsWith("/recuperar-senha")) clearAuthReturn();
    onOpenChange(false);
    navigate(completedPath, { replace: true });
  }, [completedPath, user, authLoading, navigate, onOpenChange]);

  useEffect(() => {
    if (standalone && user && !authLoading && tab === "login" && !forgotMode && !busy.current && !completedPath) setCompletedPath(target);
  }, [standalone, user, authLoading, tab, forgotMode, completedPath, target]);

  const fail = (message: string) => {
    const description = translateAuthError(message);
    setErrorMessage(description);
    toast({ title: t("errorTitle"), description, variant: "destructive" });
  };

  const runAuth = async (action: () => Promise<void>) => {
    if (busy.current) return;
    busy.current = true;
    setLoading(true);
    setErrorMessage("");
    rememberAuthReturn(target);
    try { await action(); }
    catch { fail(t("networkError")); }
    finally { busy.current = false; setLoading(false); }
  };

  const handleAuthSubmit = (data: { email: string; password: string; name?: string }, view: LoginSignupModalView) => runAuth(async () => {
    if (!data.email || !data.password) return;
    const email = data.email.trim();
    setPendingEmail(email);
    if (view === "login") {
      const { data: result, error } = await supabase.auth.signInWithPassword({ email, password: data.password });
      if (error) { fail(error.message); return; }
      if (result.session) setCompletedPath(target);
    } else {
      const { data: result, error } = await supabase.auth.signUp({
        email, password: data.password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback?next=${encodeURIComponent(target)}`,
          data: { full_name: data.name },
        },
      });
      if (error) { fail(error.message); return; }
      if (result.session) setCompletedPath(target);
      else if (result.user) { setTab("verify-otp"); }
    }
  });

  const handleVerifyOtp = (email: string, code: string) => runAuth(async () => {
    const { data, error } = await supabase.auth.verifyOtp({ email: email || pendingEmail, token: code, type: "signup" });
    if (error) { fail(error.message); return; }
    if (data.session) setCompletedPath(target);
  });

  const requestReset = (email: string) => runAuth(async () => {
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: passwordRecoveryURL(window.location.origin),
    });
    if (error) { fail(error.message); return; }
    setPendingEmail(email.trim());
    setResetSent(true);
  });

  const handleForgotPassword = (event: React.FormEvent) => {
    event.preventDefault();
    void requestReset(forgotEmail);
  };

  if (open && desktop && standalone) return <SignInPage view={tab} forgot={forgotMode} resetSent={resetSent} loading={loading || !!completedPath}
    pendingEmail={pendingEmail} error={errorMessage} checkout={target.split("?")[0] === "/checkout"}
    onSubmit={handleAuthSubmit} onVerifyOtp={handleVerifyOtp}
    onViewChange={view => { setTab(view); setForgotMode(false); setResetSent(false); setErrorMessage(""); }}
    onForgotPassword={() => { setForgotMode(true); setResetSent(false); setErrorMessage(""); }} onResetPassword={requestReset}
    onBack={() => { clearAuthReturn(); onOpenChange(false); }} />;

  if (desktop && !standalone) return null;

  if (forgotMode) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="w-full h-full max-sm:min-h-[100dvh] max-sm:rounded-none max-sm:border-none sm:h-auto sm:max-h-[92dvh] sm:max-w-[400px] sm:rounded-2xl bg-card text-foreground border border-border/80 shadow-2xl p-6 max-sm:p-5 max-sm:py-6 overflow-y-auto"
          aria-describedby="auth-forgot-desc"
          style={{ touchAction: "manipulation" }}
        >
          <DialogTitle className="sr-only">Redefinir senha</DialogTitle>
          <DialogDescription id="auth-forgot-desc" className="sr-only">
            {t(resetSent ? "resetSent.description" : "forgot.description")}
          </DialogDescription>
          <div className="flex flex-col justify-between h-full min-h-full space-y-6" style={{ touchAction: "manipulation" }}>
            <div className="flex items-center justify-between w-full flex-shrink-0">
              <button
                type="button"
                onClick={() => setForgotMode(false)}
                className="flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors select-none"
                style={{ touchAction: "manipulation" }}
              >
                <ArrowLeft className="h-4 w-4" />
                Voltar
              </button>
              <span className="text-xs text-muted-foreground font-medium select-none">
                Português (Brasil)
              </span>
            </div>

            <div className="flex-1 flex flex-col justify-center my-auto py-2 space-y-5">
              <div className="flex flex-col items-center justify-center my-2 text-center">
                <h2 className="font-display text-xl font-bold text-foreground mb-1">{t(resetSent ? "resetSent.title" : "forgot.title")}</h2>
                <p className="text-xs text-muted-foreground max-w-[280px]">
                  {t(resetSent ? "resetSent.description" : "forgot.description")}
                </p>
              </div>

              {resetSent ? <p role="status" aria-label={t("resetSent.title")} className="text-center text-sm break-words">{pendingEmail}</p> : <form onSubmit={handleForgotPassword} className="space-y-3.5 w-full" style={{ touchAction: "manipulation" }}>
                <div className="relative flex items-center h-12 w-full rounded-xl border border-border/80 bg-secondary/30 px-4 transition-all focus-within:border-primary focus-within:bg-background">
                  <input
                    id="forgot-email" aria-label="E-mail"
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="E-mail (ex: seu@email.com)"
                    required
                    className="w-full bg-transparent p-0 text-[16px] text-foreground placeholder:text-muted-foreground/60 outline-none focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus:ring-offset-0 focus-visible:ring-offset-0 border-none shadow-none"
                    style={{ fontSize: "16px", touchAction: "manipulation" }}
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full h-12 rounded-xl text-base font-semibold transition-all active:scale-[0.98] shadow-none bg-primary text-primary-foreground hover:opacity-90"
                  disabled={loading}
                  style={{ touchAction: "manipulation" }}
                >
                  {t(loading ? "forgot.loading" : "forgot.submit")}
                </Button>
              </form>}
              {errorMessage && <p role="alert" className="text-sm text-destructive">{errorMessage}</p>}
            </div>

            <div className="flex flex-col items-center gap-4 w-full flex-shrink-0 pt-4">
              <button
                type="button"
                className="w-full h-12 rounded-xl border border-primary text-primary font-semibold text-base hover:bg-primary/10 transition-colors select-none flex items-center justify-center"
                style={{ touchAction: "manipulation" }}
                onClick={() => { setForgotMode(false); setResetSent(false); setErrorMessage(""); }}
              >
                {t("backToLogin")}
              </button>

              <div className="flex items-center justify-center opacity-70 text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
                TicketHall
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <LoginSignupModal
      open={open}
      onOpenChange={onOpenChange}
      defaultView={tab}
      loading={loading}
      pendingEmail={pendingEmail}
      onViewChange={(view) => setTab(view)}
      onSubmit={handleAuthSubmit}
      onVerifyOtp={handleVerifyOtp}
      onSocialLogin={undefined}
      onForgotPassword={() => { setForgotMode(true); setResetSent(false); setErrorMessage(""); }}
    />
  );
}
